import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { HTMLAttributes, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Direct imports (not the barrel): keeps `@mui/x-date-pickers` out of the
// routed bundle — this page has no date filter.
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { PlaceSelect } from '../../../components/place';
import {
  MIN_SEARCH_LENGTH,
  SOURCE_OPTIONS,
  STATE_OPTIONS,
  type PickerFilters,
} from '../_lib/pickerFilters';

/**
 * Filter toolbar for `/pickers` (AUT-400): search, Источник, Статус, Страна + Город.
 * The mockup's «Компания» filter waits for the companies epic (CP-10b).
 * Every value lives in the URL via the parent's `setFilters`.
 */
export interface PickersFilterBarProps {
  filters: PickerFilters;
  setFilters: (
    patch: Partial<Record<keyof PickerFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

const ANY = '';

/**
 * e2e hooks on each select's clickable combobox. Typed consts, not inline
 * literals: `SelectDisplayProps` is `HTMLAttributes`, which has no `data-*`.
 */
type TestIdProps = HTMLAttributes<HTMLDivElement> & { 'data-testid': string };
const SOURCE_SELECT_PROPS: TestIdProps = { 'data-testid': 'pickers-filter-source' };
const STATE_SELECT_PROPS: TestIdProps = { 'data-testid': 'pickers-filter-state' };

export const PickersFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: PickersFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('pickers.search.placeholder')}
        testId="pickers-search-input"
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <TextField
        select
        size="small"
        label={t('pickers.filters.sourceLabel')}
        value={filters.source ?? ANY}
        onChange={(e) => setFilters({ source: e.target.value || null })}
        slotProps={{
          inputLabel: { shrink: true },
          select: {
            displayEmpty: true,
            SelectDisplayProps: SOURCE_SELECT_PROPS,
          },
        }}
        sx={{ minWidth: 150 }}
      >
        <MenuItem value={ANY}>{t('pickers.filters.any')}</MenuItem>
        {SOURCE_OPTIONS.map((s) => (
          <MenuItem key={s} value={s}>
            {t(`pickers.source.${s}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={t('pickers.filters.stateLabel')}
        value={filters.state}
        onChange={(e) => setFilters({ state: e.target.value === 'all' ? null : e.target.value })}
        slotProps={{ select: { SelectDisplayProps: STATE_SELECT_PROPS } }}
        sx={{ minWidth: 150 }}
      >
        {STATE_OPTIONS.map((s) => (
          <MenuItem key={s} value={s}>
            {t(`pickers.filters.state.${s}`)}
          </MenuItem>
        ))}
      </TextField>
      <PlaceSelect
        mode="filter"
        testId="pickers-filter-place"
        value={{
          // A legacy `?city=Минск` has no country: Belarus.
          cityCountry: filters.cityCountry ?? (filters.city ? 'BY' : ''),
          city: filters.city ?? '',
          cityCustom: false,
        }}
        onChange={(p) => setFilters({ cityCountry: p.cityCountry || null, city: p.city || null })}
      />
    </FilterBar>
  );
};
